import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/shared/ToastNotification';
import api from '../../services/api';

interface Assignment {
  id: string;
  delivery_route: { id: string; name: string };
  user: { id: string; name: string };
  assignment_date: string;
  status: string;
  total_stock_allocated: number;
}

export default function DeliveryAssignmentsIndex() {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [routes, setRoutes] = useState<{ id: string; name: string }[]>([]);
  const [partners, setPartners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({ 
    delivery_route_id: '', 
    user_id: '', 
    assignment_date: new Date().toISOString().split('T')[0],
    status: 'pending'
  });
  
  const { success, error } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assignRes, routeRes, staffRes] = await Promise.all([
        api.get('/delivery_assignments'),
        api.get('/delivery_routes'),
        api.get('/settings/staff')
      ]);
      
      if (assignRes.success) setAssignments(assignRes.data.delivery_assignments || []);
      if (routeRes.success) setRoutes(routeRes.data.delivery_routes || []);
      if (staffRes.success) {
        setPartners(staffRes.data?.data || staffRes.data || []);
      }
      
      if (!assignRes.success || !routeRes.success || !staffRes.success) {
        error('Some data failed to load');
      }
    } catch (err) {
      error('Failed to load data');
      setAssignments([]);
      setRoutes([]);
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await api.post('/delivery_assignments', { delivery_assignment: formData });
      if (res.success) {
        success('Assignment created successfully');
        setShowForm(false);
        fetchData();
      } else {
        error(res.message || (res.errors?.join(', ')) || 'Failed to create assignment');
      }
    } catch (err) {
      error('An error occurred');
    }
  };

  return (
    <div className="pt-6 space-y-8 animate-in fade-in duration-700">
      {/* Premium Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pb-2 border-b border-slate-100">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Staff <span className="text-indigo-600">Assignments</span></h1>
          <p className="text-slate-500 font-medium text-sm flex items-center gap-2">
             <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
             {assignments.length} operational routes dispatched for manual tracking
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowForm(!showForm)}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-black uppercase tracking-widest text-xs transition-all ${
              showForm 
                ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' 
                : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-xl shadow-indigo-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
            {showForm ? 'Cancel Operation' : 'Dispatch New Route'}
          </button>
        </div>
      </div>

      {/* Modern Configuration Form */}
      {showForm && (
        <div className="bg-white p-8 rounded-[2rem] border-2 border-slate-100 shadow-xl shadow-slate-200/50 animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="flex items-center gap-3 mb-8">
             <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2" /></svg>
             </div>
             <div>
                <h3 className="text-xl font-black text-slate-900 tracking-tight">Daily Logic Dispatch</h3>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Assign partners to geographic routes for commercial operations</p>
             </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Logistics Route</label>
                <div className="relative">
                  <select 
                    value={formData.delivery_route_id}
                    onChange={(e) => setFormData({ ...formData, delivery_route_id: e.target.value })}
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-bold text-slate-900 appearance-none"
                    required
                  >
                    <option value="" className="text-slate-900">Select Active Route</option>
                    {routes.map(r => <option key={r.id} value={r.id} className="text-slate-900">{r.name}</option>)}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Commercial Partner</label>
                <div className="relative">
                  <select 
                    value={formData.user_id}
                    onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-bold text-slate-900 appearance-none"
                    required
                  >
                    <option value="" className="text-slate-900">Choose Field Agent</option>
                    {partners.map(p => <option key={p.user?.id} value={p.user?.id} className="text-slate-900">{p.user?.name}</option>)}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Operation Chronology</label>
                <input 
                  type="date" 
                  value={formData.assignment_date}
                  onChange={(e) => setFormData({ ...formData, assignment_date: e.target.value })}
                  className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-bold text-slate-900"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end pt-6 border-t border-slate-100">
              <button type="submit" className="px-10 py-3.5 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-slate-200 hover:bg-black transition-all active:scale-95">
                Commit & Dispatch Logs
              </button>
            </div>
          </form>
        </div>
      )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {loading ? (
          <div className="col-span-full py-24 flex flex-col items-center justify-center space-y-4">
            <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Sychronizing Assignments...</p>
          </div>
        ) : assignments.length === 0 ? (
          <div className="col-span-full py-20 bg-white rounded-2xl border border-dashed border-slate-200 flex flex-col items-center justify-center space-y-4 shadow-sm">
            <div className="p-4 bg-slate-50 rounded-2xl text-slate-300">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </div>
            <p className="text-slate-500 font-semibold">No active assignments discovered.</p>
            <button onClick={() => setShowForm(true)} className="text-indigo-600 font-bold text-sm hover:underline">Create First Assignment</button>
          </div>
        ) : (
          assignments.map((assign) => (
            <div key={assign.id} className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-200 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden flex flex-col">
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center text-slate-500 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300 shadow-sm">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-800 leading-tight group-hover:text-indigo-700 transition-colors uppercase tracking-tight">{assign.user?.name}</h4>
                      <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-0.5">Delivery Partner</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border transition-colors ${assign.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : assign.status === 'in_progress' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-slate-50 text-slate-400 border-slate-100'}`}>
                    {assign.status.replace('_', ' ')}
                  </span>
                </div>
              
                <div className="space-y-3 mb-4 bg-slate-50/50 p-3 rounded-xl border border-slate-50">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-400 font-black uppercase tracking-wider">Route</span>
                    <span className="text-slate-800 font-bold">{assign.delivery_route?.name}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-400 font-black uppercase tracking-wider">Schedule</span>
                    <span className="text-slate-800 font-bold">{new Date(assign.assignment_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                </div>
              </div>

              <div className="px-5 pb-5 mt-auto">
                <button 
                  onClick={() => navigate(`/delivery/checklist/${assign.id}`)}
                  className="w-full py-2.5 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-600 hover:text-white rounded-xl transition-all border border-indigo-100 flex items-center justify-center gap-2"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                  Operational Checklist
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
